// ***
// Angular
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, ValidationErrors, Validators } from '@angular/forms';
import { RequestResult } from '@app/core/dto/RequestResult';
import { SessionStorageService } from '@app/core/services/session-storage.service';
import { TopBarManagerService } from '@app/core/services/topbar-manager.service';
import { RoomReservationService } from '@app/kip/services/room-reservation.service';
import { NormalizedReservation } from '@app/kip/viewdto/vwReservation/NormalizedReservation';
import { RoomReservation } from '@app/kip/viewdto/vwReservation/RoomReservation';
import { RoomState } from '@app/shared/enums/roomstates';
// 3rdparty
import dayjs from 'dayjs';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { firstValueFrom, Subscription } from 'rxjs';
import LocaleHelper from '@app/shared/helpers/LocaleHelper';
import { SpinnerService } from '@app/core/services/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { HokParamsService } from '@app/kip/services/hok-params.service';


// Hostware

@AutoUnsubscribe()
@Component({
  selector: 'room-reservation-future',
  templateUrl: './room-reservation-future.component.html',
  styleUrls: ['./room-reservation-future.component.scss']
})
export class RoomReservationFutureComponent implements OnInit, OnDestroy {
  @Input() opMode: 'dashboard' | 'page' = 'page';
  filterForm: FormGroup;
  endDate = dayjs().add(14, 'day');
  startDate = dayjs();
  monthAndSpans: MonthAndSpan[] = [];
  generatedDates: string[] = [];
  normalizedReservation: NormalizedReservation[] = [];
  reservationSummary: ReservationSummary[];
  loadByInterval$: Subscription;
  hoteUnitChanged$: Subscription;
  monthNames: string[];
  monthShortNames: string[];
  loaded: boolean = false;

  // Mat-table-höz szükséges változók
  displayedColumns: string[] = [];
  monthHeaderColumns: string[] = [];
  tableRows: any[] = [];
  arrival: string;
  departure: string;
  resident: string
  dayUse: string;

  constructor(
    private fb: FormBuilder,
    private roomReservationService: RoomReservationService,
    private sessionStorageService: SessionStorageService,
    private topBarManagerService: TopBarManagerService,
    private spinnerService: SpinnerService,
    private translateService: TranslateService,
    private hokParamService: HokParamsService
  ) {
    this.filterForm = this.fb.group({
      'fDateFrom': new FormControl(this.startDate, [Validators.required]),
      'fDateTo': new FormControl(this.endDate, [Validators.required])
    });
  }

  //#region =============== Angular Lifecyle events ============================================================================
  async ngOnInit(): Promise<void> {
    this.resident = this.translateService.instant('room_reservation_future.resident');
    this.arrival = this.translateService.instant('room_reservation_future.arrival');
    this.departure = this.translateService.instant('room_reservation_future.departure');
    this.dayUse = this.translateService.instant('room_reservation_future.day_use');
    this.monthNames = LocaleHelper.getCalendarLocale(this.sessionStorageService.getUserLanguage()).monthNames;
    this.monthShortNames = LocaleHelper.getCalendarLocale(this.sessionStorageService.getUserLanguage()).monthNamesShort;

    let froDate: string
    const froDateResult = await firstValueFrom(this.hokParamService.loadFroDatum(this.sessionStorageService.getSelectedUnit(), this.sessionStorageService.getSelectedHotelId()));
    const res = froDateResult as RequestResult;
    froDate = res.Data.toString();

    this.filterForm.controls['fDateFrom'].setValue(dayjs(froDate));
    this.filterForm.controls['fDateTo'].setValue(dayjs(froDate).add(14, 'day'));
    this.hoteUnitChanged$ = this.topBarManagerService.hotelUnitChanged.subscribe(result => {
      this.applyFilter();
    });
    this.applyFilter();
  }

  ngOnDestroy(): void {
    this.hoteUnitChanged$?.unsubscribe();
    this.loadByInterval$?.unsubscribe();
  }
  //#endregion =================================================================================================================

  //#region =============== List event hadlers   ===============================================================================
  //#endregion =================================================================================================================

  //#region =============== Form events ========================================================================================
  async applyFilter() {

    this.loaded = false;
    this.startDate = this.filterForm.controls["fDateFrom"].value;
    this.endDate = this.filterForm.controls["fDateTo"].value;
    this.spinnerService.setSpinnerVisibility(true);

    let unit = this.sessionStorageService.getSelectedUnit();
    let hotel = this.sessionStorageService.getSelectedHotelId();
    const daysToGenerate = Math.abs(this.endDate.diff(this.startDate, 'd'));
    let daysInMonth = 0;
    let monthNumber = this.startDate.add(0, 'd').month();

    this.generatedDates = [];
    this.monthAndSpans = [];

    for (let i = 0; i <= daysToGenerate; i++) {
      this.generatedDates.push(this.startDate.add(i, 'd').format("YYYY.MM.DD"));
      if (this.startDate.add(i, 'd').month() == monthNumber) {
        daysInMonth++;
      } else {
        this.monthAndSpans.push({ MonthName: this.monthName(monthNumber), SpanValue: daysInMonth, MontNumber: monthNumber });
        daysInMonth = 1;
        monthNumber = this.startDate.add(i, 'd').month();
      }
    }
    this.monthAndSpans.push({ MonthName: this.monthName(monthNumber), SpanValue: daysInMonth, MontNumber: monthNumber });

    this.loadByInterval$ = this.roomReservationService.loadByDateInterval(
      unit,
      hotel,
      this.filterForm.controls["fDateFrom"].value.format("YYYY.MM.DD"),
      this.filterForm.controls["fDateTo"].value.format("YYYY.MM.DD")
    ).subscribe(result => {
      const res = result as RequestResult;
      const reservationData = res.Data as RoomReservation[];

      //this.normalizedReservation = this.NormalizeReservationData(reservationData);
      this.reservationSummary = this.createDaily(reservationData);

      // --- Mat-table oszlopok és sorok generálása ---
      this.displayedColumns = ['category', ...this.generatedDates.map((_, i) => 'day' + i)];
      this.monthHeaderColumns = ['month-header-first', ...this.monthAndSpans.map((_, i) => 'month-' + i)];

      this.tableRows = [
        {
          label: 'room_reservation_future.arrival',
          values: this.reservationSummary.map(s => s.SumArrival)
        },
        {
          label: 'room_reservation_future.departure',
          values: this.reservationSummary.map(s => s.SumDeparture)
        },
        {
          label: 'room_reservation_future.resident',
          values: this.reservationSummary.map(s => s.SumResident)
        },
        {
          label: 'room_reservation_future.day_use',
          values: this.reservationSummary.map(s => s.SumDayUse)
        },
        {
          label: 'common.sum',
          values: this.reservationSummary.map(s => s.InTotal)
        }
      ];

      this.loaded = true;
      this.spinnerService.setSpinnerVisibility(false);
    });
  }
  //#endregion =================================================================================================================

  //#region =============== Functions  =========================================================================================

  createDaily(reservations: RoomReservation[]): ReservationSummary[] {
    let summary: ReservationSummary[] = [];
    let actItem: ReservationSummary;
    // OOO szobák törlése 
    let reservationsFiltered  = reservations.filter(res => res.Ressta !== "O");
    this.generatedDates.forEach(actDate => {
      reservationsFiltered.forEach(reservation => {
        if (!summary.find(item => item.ReservationDate == actDate)) {
          actItem = new ReservationSummary;
          actItem.ReservationDate = actDate;
          actItem.SumArrival = 0;
          actItem.SumDeparture = 0;
          actItem.SumResident = 0;
          actItem.SumDayUse = 0;
          summary.push(actItem);
        } else {
          actItem = summary.find(item => item.ReservationDate == actDate);
        }

        if (dayjs(reservation.Erkezi).isSame(dayjs(reservation.Utazik)) && dayjs(reservation.Erkezi).isSame(dayjs(actDate))) {
          if (reservation.Mennyi == 0) {
            actItem.SumDayUse++;
          } else {
            actItem.SumDayUse += reservation.Mennyi;
          }
        } else {
          if (dayjs(reservation.Erkezi).isSame(dayjs(actDate))) {
            if (reservation.Mennyi == 0) {
              actItem.SumArrival++;
            } else {
              actItem.SumArrival += reservation.Mennyi;
            }
          }
          if (dayjs(reservation.Utazik).isSame(dayjs(actDate))) {
            if (reservation.Mennyi == 0) {
              actItem.SumDeparture++;
            } else {
              actItem.SumDeparture += reservation.Mennyi;
            }
          }
          if (dayjs(reservation.Erkezi).isBefore(dayjs(actDate)) && dayjs(reservation.Utazik).isAfter(dayjs(actDate))) {
            if (reservation.Mennyi == 0) {
              actItem.SumResident++;
            } else {
              actItem.SumResident += reservation.Mennyi;
            }
          }
        }
        actItem.InTotal = actItem.SumArrival + actItem.SumDeparture + actItem.SumResident + actItem.SumDayUse;
      });
    });
    return summary;
  }

  dayOnly(dateStr: string): string {
    return dateStr.split('.')[2];
  }

  monthName(monthNumber: number): string {
    return this.monthNames[monthNumber];
  }

  isWeekend(dateValue: string) {
    const dateObj = dayjs(dateValue);
    let styles = {};
    if (dateObj.day() == 0 || dateObj.day() == 6) { // 0: vasárnap, 6: szombat
      styles["background-color"] = "#ee8484";
    }
    return styles;
  }

  iconFunction(label: string): string {
    if (label == this.arrival) {
      return 'mdi-arrow-left-bold-box';
    } else if (label == this.departure) {
      return 'mdi-arrow-right-bold-box';
    } else if (label == this.resident) {
      return 'mdi-bed';
    } else if (label == this.dayUse) {
      return 'mdi-weather-sunny';
    } else {
      return 'mdi-help-circle';
    }
  }

  iconClass(label: string): string {
    if (label == this.arrival) {
      return 'arrival-icon';
    } else if (label == this.departure) {
      return 'departure-icon';
    } else if (label == this.resident) {
      return 'resident-icon';
    } else if (label == this.dayUse) {
      return 'dayuse-icon';
    } else {
      return 'help-icon';
    }
  }

  rowStyle( row: any) {
    let styles = {};
    if(row.label == 'common.sum') {
      styles["font-weight"] = "bold";
    }
    return styles;  
  }

  filterButtonDisabled(): boolean {
    if( dayjs(this.filterForm.controls['fDateFrom'].value).isAfter(dayjs(this.filterForm.controls['fDateTo'].value))) {
      this.reservationSummary = [];
      return true;
    }
    return false;
  }
  
  //#endregion =================================================================================================================
}

class RoomExtended {
  RoomNumber: string;
  RoomType: string;
}

class ReservationSummary {
  ReservationDate: string;
  SumDayUse: number;
  SumArrival: number;
  SumDeparture: number;
  SumResident: number;
  InTotal: number;
}

class MonthAndSpan {
  MonthName: string;
  SpanValue: number;
  MontNumber: number;
}